/**
 * Brand Portfolio Glossary
 *
 * Structured definitions for every loaded term in the Brand Portfolio.
 * Each entry has:
 *   - definition: plain-language "what is this"
 *   - threshold: exact trigger rule (when applicable)
 *   - action: practical nudge for the portfolio manager
 *
 * Terminology is dual-mode: works for agency staff managing client brands
 * AND for in-house enterprise portfolio managers overseeing their own brands.
 *
 * i18n keys for label/definition/threshold/action live in agency.json.
 * The GlossaryTooltip component renders these inline; GlossaryModal
 * renders the full list.
 */

export type GlossaryCategory =
  | 'health-dimension'
  | 'health-tier'
  | 'pipeline-state'
  | 'risk-signal'
  | 'metric'
  | 'lifecycle'
  | 'capacity'
  | 'delta';

export interface GlossaryTerm {
  id: string;
  /** i18n key for the display label — falls back to labelFallback */
  labelKey: string;
  labelFallback: string;
  category: GlossaryCategory;
  /** i18n key for definition */
  definitionKey: string;
  definitionFallback: string;
  /** i18n key for threshold (rule that triggers this state) */
  thresholdKey?: string;
  thresholdFallback?: string;
  /** i18n key for action (what to do) */
  actionKey?: string;
  actionFallback?: string;
  /** Colour dot tint in the tooltip card */
  dotColor?: string;
}

/**
 * All glossary terms keyed by stable ID.
 */
export const PORTFOLIO_GLOSSARY: Record<string, GlossaryTerm> = {

  // ── Health dimensions ───────────────────────────────────────────────────────

  'health-operational': {
    id: 'health-operational',
    category: 'health-dimension',
    labelKey: 'glossary_label_health_operational',
    labelFallback: 'Operational',
    definitionKey: 'glossary_def_health_operational',
    definitionFallback:
      'Can we execute? Scored 0–100 from runway length, approval velocity, stale pending posts, channel health, and publishing reliability.',
    thresholdKey: 'glossary_threshold_health_operational',
    thresholdFallback: 'Score 0–100 — composite of five operational sub-signals.',
    actionKey: 'glossary_action_health_operational',
    actionFallback:
      'If low: unblock stuck approvals, reconnect broken channels, and refill the calendar.',
    dotColor: 'bg-primary-500',
  },

  'health-brand': {
    id: 'health-brand',
    category: 'health-dimension',
    labelKey: 'glossary_label_health_brand',
    labelFallback: 'Brand Equity',
    definitionKey: 'glossary_def_health_brand',
    definitionFallback:
      'Are we building the right thing? Scored 0–100 from content consistency (cadence vs commitment), quality (brand fit), and relevance (on-strategy output).',
    thresholdKey: 'glossary_threshold_health_brand',
    thresholdFallback: 'Score 0–100 — weighted average of consistency, quality, and relevance.',
    actionKey: 'glossary_action_health_brand',
    actionFallback:
      'If low: review the content strategy — drift is real. Check whether pillars still match the brief.',
    dotColor: 'bg-secondary-400',
  },

  'health-commercial': {
    id: 'health-commercial',
    category: 'health-dimension',
    labelKey: 'glossary_label_health_commercial',
    labelFallback: 'Commercial',
    definitionKey: 'glossary_def_health_commercial',
    definitionFallback:
      'Is this brand profitable and retained? Scored 0–100 from budget allocated, review risk, satisfaction signals, and spend adherence.',
    thresholdKey: 'glossary_threshold_health_commercial',
    thresholdFallback: 'Score 0–100 — composite of revenue and retention sub-signals.',
    actionKey: 'glossary_action_health_commercial',
    actionFallback:
      'If low: the conversation is probably about budget review or scope. Have it now, not at the cliff.',
    dotColor: 'bg-peach',
  },

  // ── Health tiers ─────────────────────────────────────────────────────────────

  'tier-on-track': {
    id: 'tier-on-track',
    category: 'health-tier',
    labelKey: 'glossary_label_tier_on_track',
    labelFallback: 'On track',
    definitionKey: 'glossary_def_tier_on_track',
    definitionFallback:
      'Everything is humming. Content is flowing, approvals are quick, and the brand relationship is healthy.',
    thresholdKey: 'glossary_threshold_tier_on_track',
    thresholdFallback: 'Overall health score 80 or above.',
    actionKey: 'glossary_action_tier_on_track',
    actionFallback:
      'Share the momentum in your weekly status — stakeholders love hearing when things are going well.',
    dotColor: 'bg-primary-500',
  },

  'tier-watch': {
    id: 'tier-watch',
    category: 'health-tier',
    labelKey: 'glossary_label_tier_watch',
    labelFallback: 'Watch',
    definitionKey: 'glossary_def_tier_watch',
    definitionFallback:
      "Not broken, but something's drifting. Keep a closer eye — a small nudge now prevents a bigger issue later.",
    thresholdKey: 'glossary_threshold_tier_watch',
    thresholdFallback: 'Overall health score 60–79.',
    actionKey: 'glossary_action_tier_watch',
    actionFallback:
      'Check which sub-signal is pulling the score down. One targeted action usually pulls it back up.',
    dotColor: 'bg-peach',
  },

  'tier-at-risk': {
    id: 'tier-at-risk',
    category: 'health-tier',
    labelKey: 'glossary_label_tier_at_risk',
    labelFallback: 'At risk',
    definitionKey: 'glossary_def_tier_at_risk',
    definitionFallback:
      'Clear signals that something is off. This brand should be your focus for the week.',
    thresholdKey: 'glossary_threshold_tier_at_risk',
    thresholdFallback: 'Overall health score 40–59.',
    actionKey: 'glossary_action_tier_at_risk',
    actionFallback:
      'Identify the top two blockers and resolve them this week. Escalate if needed.',
    dotColor: 'bg-coral-400',
  },

  'tier-off-track': {
    id: 'tier-off-track',
    category: 'health-tier',
    labelKey: 'glossary_label_tier_off_track',
    labelFallback: 'Off track',
    definitionKey: 'glossary_def_tier_off_track',
    definitionFallback:
      'Urgent. Multiple signals are red. Skip this at your peril — the brand relationship is at stake.',
    thresholdKey: 'glossary_threshold_tier_off_track',
    thresholdFallback: 'Overall health score below 40.',
    actionKey: 'glossary_action_tier_off_track',
    actionFallback:
      'Act today. Surface a plan to the stakeholder — silence at this stage reads as negligence.',
    dotColor: 'bg-coral-600',
  },

  'tier-paused': {
    id: 'tier-paused',
    category: 'health-tier',
    labelKey: 'glossary_label_tier_paused',
    labelFallback: 'Paused',
    definitionKey: 'glossary_def_tier_paused',
    definitionFallback:
      'This brand space is on hold. No active content work is expected.',
    thresholdKey: 'glossary_threshold_tier_paused',
    thresholdFallback: 'Brand lifecycle stage is set to Paused.',
    actionKey: 'glossary_action_tier_paused',
    actionFallback:
      'No action needed unless resuming. Touch base periodically to keep the relationship warm.',
    dotColor: 'bg-sage-gray',
  },

  'tier-on-hold': {
    id: 'tier-on-hold',
    category: 'health-tier',
    labelKey: 'glossary_label_tier_on_hold',
    labelFallback: 'On hold',
    definitionKey: 'glossary_def_tier_on_hold',
    definitionFallback:
      'Work is temporarily paused — often waiting on brand assets, legal sign-off, or budget approval.',
    thresholdKey: 'glossary_threshold_tier_on_hold',
    thresholdFallback: 'Manually set in the status update.',
    actionKey: 'glossary_action_tier_on_hold',
    actionFallback:
      'Note the blocker in your status update and set a follow-up date so it does not fall off the radar.',
    dotColor: 'bg-gray-400',
  },

  'tier-complete': {
    id: 'tier-complete',
    category: 'health-tier',
    labelKey: 'glossary_label_tier_complete',
    labelFallback: 'Complete',
    definitionKey: 'glossary_def_tier_complete',
    definitionFallback:
      'A campaign or budget period is finished. Used in status updates to close out a cycle.',
    thresholdKey: 'glossary_threshold_tier_complete',
    thresholdFallback: 'Manually set in the status update.',
    actionKey: 'glossary_action_tier_complete',
    actionFallback:
      'Send a wrap summary to stakeholders — celebrate the wins before moving to the next cycle.',
    dotColor: 'bg-primary-400',
  },

  // ── Pipeline states ──────────────────────────────────────────────────────────

  'pipeline-pending': {
    id: 'pipeline-pending',
    category: 'pipeline-state',
    labelKey: 'glossary_label_pipeline_pending',
    labelFallback: 'Drafts',
    definitionKey: 'glossary_def_pipeline_pending',
    definitionFallback:
      "Posts not yet on the calendar — drafts the team is still polishing, plus any waiting on a reviewer (only on brands with approval enabled).",
    thresholdKey: 'glossary_threshold_pipeline_pending',
    thresholdFallback: 'Post status = Unscheduled (drafts) or Pending (awaiting review).',
    actionKey: 'glossary_action_pipeline_pending',
    actionFallback:
      'Polish drafts and submit anything ready. On approval-gated brands: chase reviewers if pending items pile up — silence stalls the calendar.',
    dotColor: 'bg-gray-300',
  },

  'pipeline-changes': {
    id: 'pipeline-changes',
    category: 'pipeline-state',
    labelKey: 'glossary_label_pipeline_changes',
    labelFallback: 'Changes requested',
    definitionKey: 'glossary_def_pipeline_changes',
    definitionFallback:
      'A reviewer has asked for edits before this post can be approved. The creator needs to act.',
    thresholdKey: 'glossary_threshold_pipeline_changes',
    thresholdFallback: 'Post status = Changes Requested.',
    actionKey: 'glossary_action_pipeline_changes',
    actionFallback:
      'Creator needs to respond. If idle for more than 24 hours, nudge them — stale revisions delay publishing.',
    dotColor: 'bg-coral-300',
  },

  'pipeline-approved': {
    id: 'pipeline-approved',
    category: 'pipeline-state',
    labelKey: 'glossary_label_pipeline_approved',
    labelFallback: 'Approved',
    definitionKey: 'glossary_def_pipeline_approved',
    definitionFallback:
      'The post has passed review and is cleared to publish. It is sitting in the approved queue waiting to be scheduled. Carte Blanche brands skip this bucket — posts go straight to scheduled.',
    thresholdKey: 'glossary_threshold_pipeline_approved',
    thresholdFallback: 'Post status = Approved (only used on approval-gated brands).',
    actionKey: 'glossary_action_pipeline_approved',
    actionFallback:
      'Schedule or publish — do not leave approved posts sitting. They represent done work going to waste.',
    dotColor: 'bg-primary-400',
  },

  'pipeline-scheduled': {
    id: 'pipeline-scheduled',
    category: 'pipeline-state',
    labelKey: 'glossary_label_pipeline_scheduled',
    labelFallback: 'Scheduled',
    definitionKey: 'glossary_def_pipeline_scheduled',
    definitionFallback:
      'The post is queued for a specific future publish time. It will go live automatically.',
    thresholdKey: 'glossary_threshold_pipeline_scheduled',
    thresholdFallback: 'Post status = Scheduled.',
    actionKey: 'glossary_action_pipeline_scheduled',
    actionFallback:
      'Good — keep the runway healthy. Aim for at least 7 days of scheduled content ahead.',
    dotColor: 'bg-secondary-300',
  },

  // ── Risk signals ─────────────────────────────────────────────────────────────

  'signal-stale': {
    id: 'signal-stale',
    category: 'risk-signal',
    labelKey: 'glossary_label_signal_stale',
    labelFallback: 'Awaiting review',
    definitionKey: 'glossary_def_signal_stale',
    definitionFallback:
      'Posts have been waiting in approval for too long. A backlog of pending review items stalls the calendar and signals a broken review loop. Only applies to brands with the approval workflow enabled — Carte Blanche brands bypass review.',
    thresholdKey: 'glossary_threshold_signal_stale',
    thresholdFallback:
      '3 or more posts awaiting review on a non-Carte-Blanche brand with a Watch, At risk, or Off track health tier.',
    actionKey: 'glossary_action_signal_stale',
    actionFallback:
      'Review and approve, or bounce back with clear change requests. Do not let posts age.',
    dotColor: 'bg-coral-500',
  },

  'signal-deprioritization-risk': {
    id: 'signal-deprioritization-risk',
    category: 'risk-signal',
    labelKey: 'glossary_label_signal_deprioritization_risk',
    labelFallback: 'Deprioritization risk',
    definitionKey: 'glossary_def_signal_deprioritization_risk',
    definitionFallback:
      'The brand lead has flagged this brand as at risk of losing focus, budget, or attention. For agency portfolios this signals likely churn; for in-house portfolios it signals deprioritization.',
    thresholdKey: 'glossary_threshold_signal_deprioritization_risk',
    thresholdFallback: "Brand renewal record has 'atRisk' flag set to true.",
    actionKey: 'glossary_action_signal_deprioritization_risk',
    actionFallback:
      'Have the real conversation — find out what is wrong and address it head-on. Do not wait.',
    dotColor: 'bg-coral-500',
  },

  'signal-channels-broken': {
    id: 'signal-channels-broken',
    category: 'risk-signal',
    labelKey: 'glossary_label_signal_channels_broken',
    labelFallback: 'Channels broken',
    definitionKey: 'glossary_def_signal_channels_broken',
    definitionFallback:
      'One or more social accounts have expired or invalid tokens. Posts to these channels will fail silently until reconnected.',
    thresholdKey: 'glossary_threshold_signal_channels_broken',
    thresholdFallback: 'At least one channel with an expired or invalid authentication token.',
    actionKey: 'glossary_action_signal_channels_broken',
    actionFallback:
      'Reconnect in the Channels settings page. Publishing will fail otherwise — this is a hard blocker.',
    dotColor: 'bg-coral-500',
  },

  'signal-channels-expiring': {
    id: 'signal-channels-expiring',
    category: 'risk-signal',
    labelKey: 'glossary_label_signal_channels_expiring',
    labelFallback: 'Channels expiring',
    definitionKey: 'glossary_def_signal_channels_expiring',
    definitionFallback:
      'Channel tokens are about to expire. Preemptive reconnection avoids a publishing outage.',
    thresholdKey: 'glossary_threshold_signal_channels_expiring',
    thresholdFallback: 'At least one channel token expires within 7 days.',
    actionKey: 'glossary_action_signal_channels_expiring',
    actionFallback:
      'Reconnect now — a 5-minute task prevents a broken pipeline and a missed publish.',
    dotColor: 'bg-peach',
  },

  'signal-review-cliff': {
    id: 'signal-review-cliff',
    category: 'risk-signal',
    labelKey: 'glossary_label_signal_review_cliff',
    labelFallback: 'Review cliff',
    definitionKey: 'glossary_def_signal_review_cliff',
    definitionFallback:
      "The next budget review or retainer renewal is approaching. Don't wait for the cliff — start the conversation now.",
    thresholdKey: 'glossary_threshold_signal_review_cliff',
    thresholdFallback: 'Next review date is within 30 days.',
    actionKey: 'glossary_action_signal_review_cliff',
    actionFallback:
      'Start the conversation now. Last-minute reviews signal poor account management.',
    dotColor: 'bg-peach',
  },

  'signal-scheduling-cliff': {
    id: 'signal-scheduling-cliff',
    category: 'risk-signal',
    labelKey: 'glossary_label_signal_scheduling_cliff',
    labelFallback: 'Scheduling cliff',
    definitionKey: 'glossary_def_signal_scheduling_cliff',
    definitionFallback:
      "Runway and library are both near zero — there is nothing scheduled and nothing in the library waiting to be scheduled. The brand is at risk of going dark.",
    thresholdKey: 'glossary_threshold_signal_scheduling_cliff',
    thresholdFallback: 'Scheduled days = 0 and library days = 0, brand is not paused.',
    actionKey: 'glossary_action_signal_scheduling_cliff',
    actionFallback:
      'Fill the calendar immediately and add content to the library. Even fresh drafts give confidence that work is in motion.',
    dotColor: 'bg-peach',
  },

  'signal-velocity-drop': {
    id: 'signal-velocity-drop',
    category: 'risk-signal',
    labelKey: 'glossary_label_signal_velocity_drop',
    labelFallback: 'Velocity drop',
    definitionKey: 'glossary_def_signal_velocity_drop',
    definitionFallback:
      'Post output has fallen sharply compared to last week. A sudden cadence drop usually means a workflow blocker.',
    thresholdKey: 'glossary_threshold_signal_velocity_drop',
    thresholdFallback: 'Posts this week are down more than 25% vs the prior week.',
    actionKey: 'glossary_action_signal_velocity_drop',
    actionFallback:
      "Find out why before the client notices. It's usually one of: approval lag, resource gap, or creative block.",
    dotColor: 'bg-peach',
  },

  // ── Metrics ──────────────────────────────────────────────────────────────────

  'metric-health-score': {
    id: 'metric-health-score',
    category: 'metric',
    labelKey: 'glossary_label_metric_health_score',
    labelFallback: 'Health score',
    definitionKey: 'glossary_def_metric_health_score',
    definitionFallback:
      'A single number (0–100) summarising how well this brand is doing across Operational, Brand Equity, and Commercial dimensions.',
    thresholdKey: 'glossary_threshold_metric_health_score',
    thresholdFallback: 'Weighted composite: 80+ On track · 60–79 Watch · 40–59 At risk · <40 Off track.',
    actionKey: 'glossary_action_metric_health_score',
    actionFallback:
      'Click the score pill to see the full breakdown by dimension and sub-signal.',
    dotColor: 'bg-primary-400',
  },

  'metric-runway': {
    id: 'metric-runway',
    category: 'metric',
    labelKey: 'glossary_label_metric_runway',
    labelFallback: 'Runway',
    definitionKey: 'glossary_def_metric_runway',
    definitionFallback:
      "Days of content available — solid = scheduled (locked on the calendar), faded = library (ready to fill the calendar at the brand's cadence).",
    thresholdKey: 'glossary_threshold_metric_runway',
    thresholdFallback:
      "Aim for 14+ days total. Library = approved-but-unscheduled posts on approval-gated brands, or all unscheduled posts on Carte Blanche brands. Below 7 with empty library triggers a 'cliff' signal.",
    actionKey: 'glossary_action_metric_runway',
    actionFallback:
      'If runway is thin and library is empty, plan a new batch of content this week.',
    dotColor: 'bg-primary-400',
  },

  'metric-velocity': {
    id: 'metric-velocity',
    category: 'metric',
    labelKey: 'glossary_label_metric_velocity',
    labelFallback: 'Velocity',
    definitionKey: 'glossary_def_metric_velocity',
    definitionFallback:
      "Posting cadence compared to last week. A positive percentage means you're publishing more; negative means less.",
    thresholdKey: 'glossary_threshold_metric_velocity',
    thresholdFallback:
      'Posts this week vs posts last week, expressed as a signed percentage.',
    actionKey: 'glossary_action_metric_velocity',
    actionFallback:
      'Steady is the goal — sharp drops need investigation before the client asks why.',
    dotColor: 'bg-primary-400',
  },

  'metric-pipeline': {
    id: 'metric-pipeline',
    category: 'metric',
    labelKey: 'glossary_label_metric_pipeline',
    labelFallback: 'Pipeline',
    definitionKey: 'glossary_def_metric_pipeline',
    definitionFallback:
      "A visual breakdown of where this brand's content sits — drafts in the library, posts in review (when approval is on), approved, and scheduled.",
    thresholdKey: 'glossary_threshold_metric_pipeline',
    thresholdFallback:
      "Stacked bar: Drafts · Changes requested · Approved · Scheduled. Carte Blanche brands skip the review and approved buckets — drafts go directly to scheduled.",
    actionKey: 'glossary_action_metric_pipeline',
    actionFallback:
      'A healthy pipeline skews right (mostly scheduled). Lots of drafts with little scheduled means the team needs to schedule, not create.',
    dotColor: 'bg-primary-400',
  },

  // ── Lifecycle stages ─────────────────────────────────────────────────────────

  'lifecycle-active': {
    id: 'lifecycle-active',
    category: 'lifecycle',
    labelKey: 'glossary_label_lifecycle_active',
    labelFallback: 'Active',
    definitionKey: 'glossary_def_lifecycle_active',
    definitionFallback:
      'Live engagement, content flowing. This is the normal operational state for a brand.',
    thresholdKey: 'glossary_threshold_lifecycle_active',
    thresholdFallback: 'Default lifecycle state for brands with an active retainer or budget.',
    actionKey: 'glossary_action_lifecycle_active',
    actionFallback:
      'Normal ops — keep the cadence steady and the approval loop tight.',
    dotColor: 'bg-primary-500',
  },

  'lifecycle-onboarding': {
    id: 'lifecycle-onboarding',
    category: 'lifecycle',
    labelKey: 'glossary_label_lifecycle_onboarding',
    labelFallback: 'Onboarding',
    definitionKey: 'glossary_def_lifecycle_onboarding',
    definitionFallback:
      'Newly onboarded brand, still setting up channels, brand voice, and first content batches.',
    thresholdKey: 'glossary_threshold_lifecycle_onboarding',
    thresholdFallback: 'Typically the first 30–60 days after kickoff.',
    actionKey: 'glossary_action_lifecycle_onboarding',
    actionFallback:
      'Focus on setup: connect channels, lock in brand guidelines, and publish the first batch on time — first impressions stick.',
    dotColor: 'bg-peach',
  },

  'lifecycle-churning': {
    id: 'lifecycle-churning',
    category: 'lifecycle',
    labelKey: 'glossary_label_lifecycle_churning',
    labelFallback: 'Deprioritizing',
    definitionKey: 'glossary_def_lifecycle_churning',
    definitionFallback:
      'The brand lead has flagged this brand as losing focus or budget. For agencies this signals likely churn; for in-house teams it signals deprioritization. Either way, act now.',
    thresholdKey: 'glossary_threshold_lifecycle_churning',
    thresholdFallback: 'Manually set when the brand lead marks the brand at risk of losing focus or leaving.',
    actionKey: 'glossary_action_lifecycle_churning',
    actionFallback:
      'Have the conversation — do not defer. Find out the root cause and make a retention or re-prioritization plan.',
    dotColor: 'bg-coral-500',
  },

  'lifecycle-paused': {
    id: 'lifecycle-paused',
    category: 'lifecycle',
    labelKey: 'glossary_label_lifecycle_paused',
    labelFallback: 'Paused',
    definitionKey: 'glossary_def_lifecycle_paused',
    definitionFallback:
      'Brand work is temporarily on hold. No active publishing work is expected.',
    thresholdKey: 'glossary_threshold_lifecycle_paused',
    thresholdFallback: 'Set when a brand requests a break from active content work.',
    actionKey: 'glossary_action_lifecycle_paused',
    actionFallback:
      'Touch base periodically to stay warm. Silence during a pause can feel like abandonment.',
    dotColor: 'bg-gray-400',
  },

  // ── Workload capacity ────────────────────────────────────────────────────────

  'capacity-healthy': {
    id: 'capacity-healthy',
    category: 'capacity',
    labelKey: 'glossary_label_capacity_healthy',
    labelFallback: 'Healthy',
    definitionKey: 'glossary_def_capacity_healthy',
    definitionFallback:
      'This team member is carrying a sustainable brand load. Quality and cadence should be consistent.',
    thresholdKey: 'glossary_threshold_capacity_healthy',
    thresholdFallback: '1–4 active brands assigned.',
    actionKey: 'glossary_action_capacity_healthy',
    actionFallback:
      'Room to take on more if the pipeline demands it.',
    dotColor: 'bg-primary-500',
  },

  'capacity-stretched': {
    id: 'capacity-stretched',
    category: 'capacity',
    labelKey: 'glossary_label_capacity_stretched',
    labelFallback: 'Stretched',
    definitionKey: 'glossary_def_capacity_stretched',
    definitionFallback:
      'Brand load is elevated. Watch for signs of overload: missed deadlines, rising pending counts, or dropping velocity.',
    thresholdKey: 'glossary_threshold_capacity_stretched',
    thresholdFallback: '5–7 active brands assigned.',
    actionKey: 'glossary_action_capacity_stretched',
    actionFallback:
      'Check in on quality and cadence. Consider redistributing one brand if at-risk accounts appear.',
    dotColor: 'bg-peach',
  },

  'capacity-overloaded': {
    id: 'capacity-overloaded',
    category: 'capacity',
    labelKey: 'glossary_label_capacity_overloaded',
    labelFallback: 'Overloaded',
    definitionKey: 'glossary_def_capacity_overloaded',
    definitionFallback:
      'This team member has more brands than the system can sustain at full quality. Output and relationships are at risk.',
    thresholdKey: 'glossary_threshold_capacity_overloaded',
    thresholdFallback: '8 or more active brands assigned.',
    actionKey: 'glossary_action_capacity_overloaded',
    actionFallback:
      'Redistribute now — quality suffers past this line and brand deprioritization follows.',
    dotColor: 'bg-coral-500',
  },

  // ── Compare deltas ────────────────────────────────────────────────────────────

  'delta-best': {
    id: 'delta-best',
    category: 'delta',
    labelKey: 'glossary_label_delta_best',
    labelFallback: 'Best',
    definitionKey: 'glossary_def_delta_best',
    definitionFallback:
      'This brand scores highest among the brands you are comparing for this metric.',
    thresholdKey: 'glossary_threshold_delta_best',
    thresholdFallback: 'Comparison context only — requires at least 2 brands with a score spread > 5 points.',
    actionKey: 'glossary_action_delta_best',
    actionFallback:
      "Use it as a benchmark. What is this brand doing right that the others aren't?",
    dotColor: 'bg-primary-500',
  },

  'delta-needs': {
    id: 'delta-needs',
    category: 'delta',
    labelKey: 'glossary_label_delta_needs',
    labelFallback: 'Needs',
    definitionKey: 'glossary_def_delta_needs',
    definitionFallback:
      'This brand scores lowest among the brands you are comparing for this metric.',
    thresholdKey: 'glossary_threshold_delta_needs',
    thresholdFallback: 'Comparison context only — requires at least 2 brands with a score spread > 5 points.',
    actionKey: 'glossary_action_delta_needs',
    actionFallback:
      'This is where to focus attention. The gap to the benchmark is the size of the opportunity.',
    dotColor: 'bg-coral-500',
  },

  // ── Asset Inventory ──────────────────────────────────────────────────────────

  'metric-asset-inventory': {
    id: 'metric-asset-inventory',
    category: 'metric',
    labelKey: 'glossary_label_metric_asset_inventory',
    labelFallback: 'Asset inventory',
    definitionKey: 'glossary_def_metric_asset_inventory',
    definitionFallback:
      'All content for this brand, grouped by workflow stage — from early drafts to remixable back-catalog.',
    thresholdKey: 'glossary_threshold_metric_asset_inventory',
    thresholdFallback: 'Not score-based; it is a snapshot of totals.',
    actionKey: 'glossary_action_metric_asset_inventory',
    actionFallback:
      'Balance matters: too much stuck in drafts = bottleneck; thin "ready" + "scheduled" = plan a batch.',
    dotColor: 'bg-primary-500',
  },

  'asset-stage-drafts': {
    id: 'asset-stage-drafts',
    category: 'pipeline-state',
    labelKey: 'glossary_label_asset_stage_drafts',
    labelFallback: 'Drafts polishing',
    definitionKey: 'glossary_def_asset_stage_drafts',
    definitionFallback:
      'Content in early stages — being written, reviewed, or revised. Not yet approved.',
    thresholdKey: 'glossary_threshold_asset_stage_drafts',
    thresholdFallback: 'Post status = Unscheduled with approval pending or changes requested.',
    actionKey: 'glossary_action_asset_stage_drafts',
    actionFallback: 'If drafts pile up, there is a bottleneck — unblock review.',
    dotColor: 'bg-peach',
  },

  'asset-stage-ready': {
    id: 'asset-stage-ready',
    category: 'pipeline-state',
    labelKey: 'glossary_label_asset_stage_ready',
    labelFallback: 'Ready to publish',
    definitionKey: 'glossary_def_asset_stage_ready',
    definitionFallback:
      'Approved content sitting in library — ready to be scheduled.',
    thresholdKey: 'glossary_threshold_asset_stage_ready',
    thresholdFallback: 'Approval = approved AND no scheduled date yet.',
    actionKey: 'glossary_action_asset_stage_ready',
    actionFallback: 'Move to the calendar to strengthen runway.',
    dotColor: 'bg-primary-500',
  },

  'asset-stage-scheduled': {
    id: 'asset-stage-scheduled',
    category: 'pipeline-state',
    labelKey: 'glossary_label_asset_stage_scheduled',
    labelFallback: 'Scheduled',
    definitionKey: 'glossary_def_asset_stage_scheduled',
    definitionFallback:
      'Approved and locked onto the calendar — will publish automatically at the set time.',
    thresholdKey: 'glossary_threshold_asset_stage_scheduled',
    thresholdFallback: 'Post has a future scheduled date and is fully approved.',
    actionKey: 'glossary_action_asset_stage_scheduled',
    actionFallback: 'Keep the runway healthy — aim for 7+ days scheduled ahead at all times.',
    dotColor: 'bg-secondary-500',
  },

  'asset-stage-published': {
    id: 'asset-stage-published',
    category: 'pipeline-state',
    labelKey: 'glossary_label_asset_stage_published',
    labelFallback: 'Published',
    definitionKey: 'glossary_def_asset_stage_published',
    definitionFallback:
      'Content that has gone live on the platform. Builds the brand archive and fuels future remix opportunities.',
    thresholdKey: 'glossary_threshold_asset_stage_published',
    thresholdFallback: 'Post status = Published.',
    actionKey: 'glossary_action_asset_stage_published',
    actionFallback: 'Track performance — top posts become candidates for repurposing.',
    dotColor: 'bg-soft-mint',
  },

  'asset-stage-remixable': {
    id: 'asset-stage-remixable',
    category: 'pipeline-state',
    labelKey: 'glossary_label_asset_stage_remixable',
    labelFallback: 'Remixable',
    definitionKey: 'glossary_def_asset_stage_remixable',
    definitionFallback:
      'Published content old enough + high-performing enough to be repurposed without feeling repetitive.',
    thresholdKey: 'glossary_threshold_asset_stage_remixable',
    thresholdFallback: '30+ days since publish and top 25% performance for the brand.',
    actionKey: 'glossary_action_asset_stage_remixable',
    actionFallback:
      'Pull into the Repurposing strip and spin a fresh angle.',
    dotColor: 'bg-coral-400',
  },

  // ── Low pipeline signal ──────────────────────────────────────────────────────

  'signal-low-pipeline': {
    id: 'signal-low-pipeline',
    category: 'risk-signal',
    labelKey: 'glossary_label_signal_low_pipeline',
    labelFallback: 'Low pipeline',
    definitionKey: 'glossary_def_signal_low_pipeline',
    definitionFallback:
      'Less than 2 weeks of planned and ready content. Time to produce the next round before the calendar goes quiet.',
    thresholdKey: 'glossary_threshold_signal_low_pipeline',
    thresholdFallback:
      "Planned days (at the brand's weekly rhythm) plus ready content not yet planned add up to 14 days or less.",
    actionKey: 'glossary_action_signal_low_pipeline',
    actionFallback:
      'Brief the creator, kick off production, or schedule a planning session this week.',
    dotColor: 'bg-peach',
  },

  // ── Repurposing ──────────────────────────────────────────────────────────────

  'metric-repurpose-opportunity': {
    id: 'metric-repurpose-opportunity',
    category: 'metric',
    labelKey: 'glossary_label_metric_repurpose_opportunity',
    labelFallback: 'Repurposing Opportunity',
    definitionKey: 'glossary_def_metric_repurpose_opportunity',
    definitionFallback:
      'A past post that performed above average and is old enough to remix without feeling repetitive.',
    thresholdKey: 'glossary_threshold_metric_repurpose_opportunity',
    thresholdFallback:
      'Published 30+ days ago, top 25% performance for its brand, and not yet remixed.',
    actionKey: 'glossary_action_metric_repurpose_opportunity',
    actionFallback:
      'Pick an angle — update data, flip format, translate for a new market, or spin a follow-up.',
    dotColor: 'bg-primary-400',
  },

} as const;

/** Ordered category labels for glossary modal grouping */
export const GLOSSARY_CATEGORY_LABELS: Record<GlossaryCategory, string> = {
  'health-dimension': 'Health Dimensions',
  'health-tier': 'Health Tiers',
  'pipeline-state': 'Pipeline States',
  'risk-signal': 'Risk Signals',
  'metric': 'Metrics',
  'lifecycle': 'Lifecycle Stages',
  'capacity': 'Workload Capacity',
  'delta': 'Compare Deltas',
};

export const GLOSSARY_CATEGORY_ORDER: GlossaryCategory[] = [
  'health-dimension',
  'health-tier',
  'pipeline-state',
  'risk-signal',
  'metric',
  'lifecycle',
  'capacity',
  'delta',
];
